import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, Receipt, TrendingUp, BarChart3, ArrowUpRight
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { usePerms } from '../permissions/PermissionsContext';

export default function FinanceManagement() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const menuItems = [
    {
      id: 'revenue-reports',
      titleAr: 'تقارير الإيرادات',
      titleEn: 'Revenue Reports',
      descAr: 'تقارير مفصلة عن الإيرادات',
      descEn: 'Detailed revenue reports',
      icon: TrendingUp,
      route: '/finance/revenue-reports',
      color: '#10B981',
      comingSoon: true
    },
    {
      id: 'pending-payments',
      titleAr: 'المدفوعات المعلقة',
      titleEn: 'Pending Payments',
      descAr: 'متابعة المدفوعات غير المكتملة',
      descEn: 'Track incomplete payments',
      icon: Receipt,
      route: '/finance/pending-payments',
      color: '#F59E0B',
      comingSoon: true
    },
    {
      id: 'financial-stats',
      titleAr: 'الإحصائيات المالية',
      titleEn: 'Financial Statistics',
      descAr: 'مجموع الرواتب ومدفوعات الشركة في مكان واحد',
      descEn: 'Total salaries + company payments at a glance',
      icon: BarChart3,
      route: '/dashboard/finance/statistics',
      color: '#8B5CF6',
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        title={language === 'ar' ? 'الإدارة المالية' : 'Financial Management'}
        subtitle={language === 'ar' ? 'إدارة المدفوعات والتقارير' : 'Payments & Reports Management'}
        icon={CreditCard}
        iconColor="#10b981"
        showBackButton={true}
        backPath="/dashboard/finance/cloudgm/customers"
      />

      {/* Main Content */}
      <main className="relative z-10">
        {/* Breadcrumb with line */}
        <div className="border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <BreadcrumbNav items={[
              { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
              { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
              { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
              { label_ar: 'منصة العملاء (المالية)', label_en: 'Customer Platform (Finance)', path: '/dashboard/finance/cloudgm/customers' },
              { label_ar: 'الإدارة المالية', label_en: 'Financial Management', path: null }
            ]} />
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Menu Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {visiblePages(menuItems, 'route').map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  onClick={() => {
                    if (item.comingSoon) {
                      // Show coming soon toast
                    } else {
                      navigate(item.route);
                    }
                  }}
                  className={`group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[${item.color}]/50 transition-all duration-300 cursor-pointer overflow-hidden ${item.comingSoon ? 'opacity-60' : ''}`}
                  data-testid={`btn-${item.id}`}
                >
                  {/* Coming Soon Badge */}
                  {item.comingSoon && (
                    <span className="absolute top-3 left-3 bg-white/10 text-[#71717A] text-xs px-2 py-1 rounded-full">
                      {language === 'ar' ? 'قريباً' : 'Soon'}
                    </span>
                  )}

                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${item.color}20`, borderColor: `${item.color}30`, borderWidth: '1px' }}
                    >
                      <Icon className="h-7 w-7" style={{ color: item.color }} />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                        {language === 'ar' ? item.titleAr : item.titleEn}
                        {!item.comingSoon && (
                          <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: item.color }} />
                        )}
                      </h3>
                      <p className="text-[#71717A] text-sm">
                        {language === 'ar' ? item.descAr : item.descEn}
                      </p>
                    </div>
                  </div>

                  {/* Stats (if available) */}
                  {item.stats && (
                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                      <span className="text-[#71717A] text-xs">{item.stats.label}</span>
                      <span className="text-white font-bold" style={{ color: item.color }}>{item.stats.value}</span>
                    </div>
                  )}

                  {/* Hover Glow Effect */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{ 
                      background: `radial-gradient(circle at 50% 50%, ${item.color}10 0%, transparent 70%)` 
                    }}
                  />
                </motion.div>
              );
            })}
          </div>

        </div>
      </main>
    </div>
  );
}
